export const SITE_URL = "https://www.sploratours.com";
export const SEO_LANGUAGES = ["en", "de", "fr", "it", "es"] as const;
export type SeoLanguage = typeof SEO_LANGUAGES[number];
type Equivalents = Partial<Record<SeoLanguage, string>>;

// Only actual translations belong in one reciprocal alternate cluster.
export const SEO_ROUTE_GROUPS: Equivalents[] = [
  { en: "/sal-island-tours-excursions", de: "/de/sal-touren-ausfluege", fr: "/fr/sal-tours-excursions", it: "/it/sal-tour-escursioni", es: "/es/sal-tours-excursiones" },
  { en: "/boa-vista-tours-excursions", de: "/de/boa-vista-touren-ausfluege", fr: "/fr/boa-vista-tours-excursions", it: "/it/boa-vista-tour-escursioni", es: "/es/boa-vista-tours-excursiones" },
  { en: "/fogo-volcano-tours-hiking", de: "/de/fogo-vulkan-trekking", fr: "/fr/fogo-volcan-trekking", it: "/it/fogo-vulcano-trekking", es: "/es/fogo-volcan-trekking" },
  { en: "/santiago-praia-cidade-velha-tours", de: "/de/santiago-praia-cidade-velha", fr: "/fr/santiago-praia-cidade-velha", it: "/it/santiago-praia-cidade-velha-tour", es: "/es/santiago-praia-cidade-velha" },
  { de: "/de/kapverden-urlaub-reisen" },
  { en: "/lp/cape-verde-vacation-from-usa" },
  { en: "/lp/cape-verde-heritage-trip-for-cape-verdeans-abroad" },
  { de: "/lp/kapverden-urlaub-deutschland-schweiz-niederlande" },
  { en: "/sao-vicente-tours-excursions" },
  { en: "/santo-antao-hiking-tours-excursions" },
  { en: "/best-tour-operator-cape-verde" },
  { en: "/cape-verde-travel-safety-guide" },
  { en: "/how-to-plan-cape-verde-trip" },
  { en: "/cape-verde-holidays" },
  { en: "/chinese-speaking-tour-guide-cape-verde" },
];

export function cleanSeoPath(value: string): string {
  const path = new URL(value, SITE_URL).pathname;
  return path.replace(/\/+$/, "") || "/";
}

export function stripSeoLanguage(path: string): string {
  return cleanSeoPath(path).replace(/^\/(en|de|fr|it|es)(?=\/|$)/, "") || "/";
}

export function getSeoLanguage(path: string): SeoLanguage {
  const clean = cleanSeoPath(path);
  const group = SEO_ROUTE_GROUPS.find((entry) => Object.values(entry).includes(clean));
  const explicit = group && SEO_LANGUAGES.find((lang) => group[lang] === clean);
  if (explicit) return explicit;
  const segment = clean.split("/")[1];
  return SEO_LANGUAGES.find((lang) => lang === segment) || "en";
}

export function getSeoAlternates(path: string): Equivalents {
  const clean = cleanSeoPath(path);
  const base = stripSeoLanguage(clean);
  const group = SEO_ROUTE_GROUPS.find((entry) =>
    Object.values(entry).some((candidate) => candidate === clean || candidate === base || stripSeoLanguage(candidate) === base));
  if (group) return group;
  return Object.fromEntries(SEO_LANGUAGES.map((lang) => [lang,
    lang === "en" ? base : `/${lang}${base === "/" ? "" : base}`])) as Equivalents;
}

export function getSeoPath(path: string, lang: SeoLanguage): string {
  const alternates = getSeoAlternates(path);
  return alternates[lang] || alternates.en || Object.values(alternates)[0] || "/";
}

/** Normalize only internal schema URLs; entity identifiers and external URLs stay stable. */
export function localizeSchema(value: unknown, lang: SeoLanguage, key = ""): unknown {
  if (Array.isArray(value)) return value.map((item) => localizeSchema(item, lang, key));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([name, item]) => [name, localizeSchema(item, lang, name)]));
  }
  if (typeof value !== "string" || !/^(https?:\/\/([^/]+\.)?sploratours\.com(?=\/|$)|\/(?!\/))/.test(value)) return value;
  const url = new URL(value, SITE_URL);
  if (!['sploratours.com', 'www.sploratours.com'].includes(url.hostname)) return value;
  url.protocol = "https:";
  url.hostname = "www.sploratours.com";
  const isPage = ["url", "item", "@id", "mainEntityOfPage"].includes(key) && !url.hash && !/\.[a-z0-9]+$/i.test(url.pathname);
  if (isPage) url.pathname = getSeoPath(url.pathname, lang);
  return url.href;
}